import { Dialog, Transition, TransitionChild } from '@headlessui/react';
import classNames from 'classnames';
import React, { Fragment, createContext, useContext } from 'react';
import { BottomSheet } from '~/components/ui/nucleus/Sheets/BottomSheet';
import { useBreakpoint } from '~/hooks/useBreakpoint';

type TContext = {
  onClose?: () => void;
};

type Props = {
  children: React.ReactNode;
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  /** When true, this sheet is pushed back visually to allow a nested sheet to appear on top */
  stacked?: boolean;
};

export function PopupDialogNew({
  children,
  isOpen,
  onClose,
  title,
  stacked = false,
}: Props) {
  const { isMobile } = useBreakpoint();

  if (isMobile) {
    return (
      <BottomSheet
        isOpen={isOpen}
        onClose={onClose}
        title={title}
        stacked={stacked}
      >
        {children}
      </BottomSheet>
    );
  }

  return (
    <Context.Provider value={{ onClose }}>
      <Transition appear show={isOpen} as={Fragment}>
        <Dialog as='div' className='fixed inset-0 z-[10000]' onClose={onClose}>
          <div className='flex h-full items-center justify-center p-4'>
            <TransitionChild
              enter='ease-out duration-300'
              enterFrom='opacity-0'
              enterTo='opacity-100'
              leave='ease-in duration-400'
              leaveFrom='opacity-100'
              leaveTo='opacity-0'
            >
              <div
                onClick={onClose}
                className='fixed inset-0 bg-slate-800 bg-opacity-60 lg:bg-opacity-20'
              />
            </TransitionChild>

            <TransitionChild
              enter='ease-out duration-300 origin-center'
              enterFrom='opacity-0'
              enterTo='opacity-100'
              leave='ease-in duration-300'
              leaveFrom='opacity-100'
              leaveTo='opacity-0'
            >
              <div className='relative flex max-h-full min-h-0 w-full max-w-[800px] flex-col overflow-hidden rounded-2xl bg-white text-left'>
                {title && <PopupDialogNew.Title>{title}</PopupDialogNew.Title>}
                {children}
              </div>
            </TransitionChild>
          </div>
        </Dialog>
      </Transition>
    </Context.Provider>
  );
}

type TitleProps = {
  children: React.ReactNode;
  /** Edit button clicked */
  onEdit?: () => void;
  loading?: boolean;
};

const Context = createContext({} as TContext);

function usePopupDialogNewContext() {
  const context = useContext(Context);
  if (!context) {
    throw new Error('Must be rendered within PopupDialogContext');
  }
  return context;
}

function Title({ children, loading = false }: TitleProps) {
  const { onClose } = usePopupDialogNewContext();

  return (
    <div className='z-10 box-border hidden w-full grid-cols-[35px_minmax(0,_1fr)_35px] items-center justify-start bg-white p-4 pb-4 pt-5 lg:grid lg:pb-4 lg:pt-7'>
      <div
        className={classNames('h-10 w-10', {
          'text-grey-30': loading,
          'text-grey-50 hover:text-brand': !loading,
        })}
      ></div>
      <div className='mx-1 flex-1 overflow-hidden break-words pb-1.5 text-center text-xl font-medium text-copy-head'>
        {children}
      </div>{' '}
      <button
        type='button'
        onClick={onClose}
        className={classNames('x h-full w-full', {
          'cursor-default': loading,
        })}
        disabled={loading}
      ></button>
    </div>
  );
}

PopupDialogNew.Title = Title;
